






























































































import { computed, defineComponent, PropType, ref, toRef, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { Message } from "@scrile/api-provider/dist/projects/scrileConnect/MessagesProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities, checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useMessageView from "@/hooks/useMessageView";
import emitter from "@/lib/emitter";
import { isAudioFile } from "@/lib/fileHelpers";
import { formatMoneyString, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { PurchaseInfo } from "@/payments/types";
import config from "@/config";
import { BlockingStatus } from "@/types";
import { EVENTS, ROLES } from "@/const";
import AttachmentsInfo from "@/components/AttachmentsInfo";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import LinkifiedText from "@/components/LinkifiedText";
import PaymentConfirmation from "@/components/PaymentConfirmation";
import UnlockForButton from "@/components/UnlockForButton";
import MessageItemAttachment from "./MessageItemAttachment.vue";
import MessageItemLink from "../../components/MessageItemLink";

export default defineComponent({
  name: "MessageItemView",
  components: {
    AttachmentsInfo,
    PaymentConfirmation,
    BaseIcon,
    UnlockForButton,
    MessageItemLink,
    BaseConfirm,
    BaseButton,
    BaseIconButton,
    MessageItemAttachment,
    LinkifiedText,
  },
  props: {
    paymentPackages: { type: Array as PropType<PaymentPackage[]>, required: true },
    userId: { type: String, required: true },
    message: { type: Object as PropType<Message>, required: true },
    blockingStatus: { type: Object as PropType<BlockingStatus>, required: true },
    markAsSeen: { type: Boolean, required: true },
    loading: { type: Boolean, default: false },
    purchaseInfoDetails: { type: Object as PropType<PurchaseInfo>, required: true },
    authUserId: { type: String, required: true },
    noInteractMode: { type: Boolean },
  },
  setup(props, { emit }) {
    const { isAuthor, isOpponentMessage, hoursMinutes, fullTime } = useMessageView(
      toRef(props, "message"),
      props.authUserId
    );
    const { authorities } = useAuthUser();

    const showUnlock = ref(false);
    const isLikeAnimated = ref(false);

    const isAudioMessage = computed(
      () =>
        !!props.message.attachment[0]?.file &&
        isAudioFile(props.message.attachment[0]?.file) &&
        props.message.attachment.length === 1
    );
    const hasAttachment = computed(() => !!props.message.attachment.length);
    const likeIcon = computed(() => (!!props.message.likes.count ? "sci-heart" : "sci-heart-outline"));
    const renderLikeIcon = computed(
      () => !!props.message.likes.count || (isOpponentMessage.value && !props.noInteractMode)
    );
    const isMessageLiked = computed(() => !!props.message.likes.count);

    const likeAnimationToggle = (messageId: string) => {
      if (messageId !== props.message.id) return;
      isLikeAnimated.value = isMessageLiked.value;
    };

    const onLikeClick = debounce(() => {
      if (isAuthor.value) return;
      emit("toggleLike");
    }, 200);

    const oneTimeCost = computed(
      () =>
        props.paymentPackages
          .find(pkg => pkg.id === props.message.attachmentPricePackageId)
          ?.costs.find(cost => cost.currency === getMainCurrencyCode())?.cost || 0
    );

    const renderMessageMenu = computed(() => {
      if (props.noInteractMode) return false;
      return isOpponentMessage.value
        ? checkAuthorities(authorities.value, ["deleteAnyMessage"])
        : checkAuthorities(authorities.value, ["deleteMyMessage"]) &&
            !props.message.attachmentPurchasers?.includes(props.userId);
    });

    const hasPrice = computed(() => {
      return props.message.attachmentPricePackageId !== "0";
    });

    const isAvailable = computed(() => {
      if (isOpponentMessage.value) {
        return props.message.attachmentAvailable ?? false;
      }
      const purchased = !!props.message.attachmentPurchasers?.find(id => id === props.userId);
      return !hasPrice.value || purchased;
    });

    const showUnlockButton = computed(
      () => hasAttachment.value && !props.message.attachmentAvailable && !props.blockingStatus.authUser
    );

    const price = computed(() => formatMoneyString(oneTimeCost.value, { currencyCode: getMainCurrencyCode() }));

    const linksValidation = computed(() => {
      return (
        config.featureClickableUserLinks &&
        (!!(props.message?.senderUser as UserPublicData)?.verified ||
          checkRole(props.message?.senderUser?.role || "", ROLES.ADMIN))
      );
    });

    const allowExternalLinks = computed(() => {
      return config.featureClickableUserExternalLinks;
    });

    const attachmentClick = async () => {
      if (isAvailable.value || isAuthor.value || props.blockingStatus.authUser) return;
      if (isOpponentMessage && props.message.senderUser?.disabled) {
        emitter.emit(EVENTS.STATUS_SHOW, { text: "This action is forbidden" });
        return;
      }
      showUnlock.value = true;
    };

    watch(toRef(props, "message"), () => {
      likeAnimationToggle(props.message.id);
    });

    return {
      isAuthor,
      isOpponentMessage,
      isAudioMessage,
      showUnlock,
      showUnlockButton,
      renderMessageMenu,
      isAvailable,
      hasPrice,
      price,
      fullTime,
      hoursMinutes,
      allowExternalLinks,
      linksValidation,
      oneTimeCost,
      likeIcon,
      renderLikeIcon,
      isLikeAnimated,
      isMessageLiked,
      hasAttachment,
      attachmentClick,
      onLikeClick,
    };
  },
});
